if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          initializeDiscountPriceHintTest();
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          setTimeout(() => {
            document.querySelectorAll('product-form').forEach((form) => {
              if (typeof form.checkLimitedProduct === 'function') {
                form.checkLimitedProduct();
              }
            });
          }, 0);

        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      async updateLimitedEditionState() {
        const variantId = this.productForm?.variantIdInput?.value;
        if (!variantId) return;

        const addButton = this.querySelector('[name="add"]');
        if (!addButton) return;

        // 当前 Variant 是否是限定款

        const isLimited =
        this.querySelector('[data-limited-edition]')?.value === 'true';

        if (!isLimited) {
          addButton.removeAttribute('disabled');
          return;
        }

        try {
          const cart = await fetch('/cart.js').then((r) => r.json());

          const exists = cart.items.some(
            (item) => item.variant_id == variantId
          );

          if (exists) {
            addButton.setAttribute('disabled', 'disabled');
            addButton.querySelector('span').textContent =
            'Limit to 1 per customer';
          } else {
            addButton.removeAttribute('disabled');
            addButton.querySelector('span').textContent =
            window.variantStrings.addToCart;
          }
        } catch (e) {
          console.error(e);
        }
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

function isHalloweenCampaignEnabled() {
  return Boolean(document.querySelector('product-info[data-halloween-campaign="true"]'));
}

function findInOpenShadowRoots(selector, root = document) {
  const directMatch = root.querySelector(selector);
  if (directMatch) return directMatch;

  for (const element of root.querySelectorAll('*')) {
    if (!element.shadowRoot) continue;

    const match = findInOpenShadowRoots(selector, element.shadowRoot);
    if (match) return match;
  }

  return null;
}

function withFormTeaser(callback, attemptsRemaining = 10) {
  if (isHalloweenCampaignEnabled()) return;

  const teaser = findInOpenShadowRoots('[data-testid="form-teaser"]');
  if (teaser) {
    callback(teaser);
    return;
  }

  if (attemptsRemaining === 0) return;
  setTimeout(() => withFormTeaser(callback, attemptsRemaining - 1), 250);
}

window.bsDiscountPriceHintTeaserObserver = window.bsDiscountPriceHintTeaserObserver || null;

function keepShopifyFormsTeaserHidden(attemptsRemaining = 10) {
  if (isHalloweenCampaignEnabled()) return;

  if (window.bsDiscountPriceHintTeaserObserver) return;

  const formRoot = findInOpenShadowRoots('[data-form-root="true"]');
  if (!formRoot) {
    if (attemptsRemaining === 0) return;
    setTimeout(() => keepShopifyFormsTeaserHidden(attemptsRemaining - 1), 250);
    return;
  }

  const selector = '[data-testid="form-teaser"]';
  const shadowRoot = formRoot.getRootNode();
  const observationRoot = shadowRoot instanceof ShadowRoot ? shadowRoot : formRoot;
  const hideTeaser = (root) => {
    const teaser = root.matches?.(selector) ? root : findInOpenShadowRoots(selector, root);
    if (teaser && teaser.style.display !== 'none') teaser.style.display = 'none';
  };

  hideTeaser(observationRoot);

  window.bsDiscountPriceHintTeaserObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes') {
        if (mutation.target.matches?.(selector)) hideTeaser(mutation.target);
        continue;
      }

      for (const node of mutation.addedNodes) {
        if (typeof node.querySelector !== 'function') continue;
        hideTeaser(node);
      }
    }
  });

  window.bsDiscountPriceHintTeaserObserver.observe(observationRoot, {
    attributes: true,
    attributeFilter: ['style'],
    childList: true,
    subtree: true,
  });
}

window.bsDiscountPriceHintExposureState = window.bsDiscountPriceHintExposureState || {
  pending: false,
  sent: false,
};

function trackDiscountPriceHintExposure(variant) {
  if (isHalloweenCampaignEnabled()) return;

  const state = window.bsDiscountPriceHintExposureState;
  if (state.pending || state.sent) return;

  const exposureKey = 'bs_discount_price_hint_exposure_v1';

  try {
    const exposedVariant = window.sessionStorage.getItem(exposureKey);
    if (exposedVariant === 'A' || exposedVariant === 'B') {
      state.sent = true;
      return;
    }
  } catch (error) {
    // Continue with the in-memory guard for this page load.
  }

  state.pending = true;

  const sendExposure = (attemptsRemaining = 10) => {
    if (isHalloweenCampaignEnabled()) {
      state.pending = false;
      return;
    }

    if (typeof window.gtag === 'function') {
      try {
        window.gtag('event', 'bs_ab_test_exposure', {
          experiment_name: 'discount_price_hint_v1',
          experiment_variant: variant,
        });
      } catch (error) {
        state.pending = false;
        return;
      }

      state.sent = true;
      state.pending = false;

      try {
        window.sessionStorage.setItem(exposureKey, variant);
      } catch (error) {
        // The in-memory guard still prevents duplicate sends on this page load.
      }
      return;
    }

    if (attemptsRemaining === 0) {
      state.pending = false;
      return;
    }

    setTimeout(() => sendExposure(attemptsRemaining - 1), 250);
  };

  sendExposure();
}

function initializeDiscountPriceHintTest() {
  if (isHalloweenCampaignEnabled()) return;

  const module = document.querySelector('[data-discount-price-test]');
  const freeGift = document.querySelector('[data-discount-test-free-gift]');

  if (!module) {
    if (freeGift && document.documentElement.dataset.discountPriceHintTestVariant === 'B') {
      freeGift.hidden = true;
    }
    return;
  }

  const storageKey = 'bs_discount_price_hint_test_v1';

  try {
    let variant = window.localStorage.getItem(storageKey);

    if (variant !== 'A' && variant !== 'B') {
      variant = Math.random() < 0.5 ? 'A' : 'B';
      window.localStorage.setItem(storageKey, variant);
    }

    document.documentElement.dataset.discountPriceHintTestVariant = variant;
    module.hidden = variant !== 'B';
    if (freeGift) freeGift.hidden = variant === 'B';

    if (variant === 'B') keepShopifyFormsTeaserHidden();
    trackDiscountPriceHintExposure(variant);
  } catch (error) {
    return;
  }
}

initializeDiscountPriceHintTest();

function getDiscountPriceHintClickModule() {
  const mainProduct = Array.from(document.querySelectorAll('main product-info[id^="MainProduct-"]')).find(
    (product) => product.getClientRects().length > 0
  );
  return mainProduct?.querySelector('[data-discount-price-test]') || null;
}

function isDiscountOfferFormTeaser(teaser) {
  const root = teaser.getRootNode();
  if (!(root instanceof ShadowRoot)) return false;
  if (!findInOpenShadowRoots('[data-form-root="true"]', root)) return false;

  // Fail closed if Shopify changes the offer's name; never count a different Forms teaser.
  const labelledBy = teaser.getAttribute('aria-labelledby');
  const labelledName = labelledBy
    ? labelledBy.trim().split(/\s+/).map((id) => root.getElementById(id)?.textContent || '').join(' ')
    : '';
  const name = (labelledName || teaser.getAttribute('aria-label') || teaser.textContent || '')
    .trim()
    .replace(/\s+/g, ' ');
  return /^get 10% off[!.]?$/i.test(name);
}

function trackDiscountOfferClick(variant, location) {
  if (isHalloweenCampaignEnabled()) return;

  // Snapshot each click independently; exposure's session guard must not suppress clicks.
  const parameters = {
    experiment_name: 'discount_price_hint_v1',
    experiment_variant: variant,
    click_location: location,
  };
  const sendClick = (attemptsRemaining = 10) => {
    if (isHalloweenCampaignEnabled()) return;

    if (typeof window.gtag === 'function') {
      try {
        window.gtag('event', 'bs_discount_offer_click', parameters);
      } catch (error) {
        // Analytics must never interfere with opening the form.
      }
      return;
    }

    if (attemptsRemaining === 0) return;
    setTimeout(() => sendClick(attemptsRemaining - 1), 250);
  };
  sendClick();
}

if (!window.bsDiscountOfferTeaserClickListenerRegistered) {
  document.addEventListener('click', (event) => {
    if (isHalloweenCampaignEnabled()) return;
    if (event.isTrusted !== true || document.documentElement.dataset.discountPriceHintTestVariant !== 'A') return;
    if (!getDiscountPriceHintClickModule()) return;

    // The document target is retargeted to the host; the path includes open-shadow elements.
    const teaser = event.composedPath().find((node) => node.matches?.('[data-testid="form-teaser"]'));
    if (!teaser || !isDiscountOfferFormTeaser(teaser)) return;

    trackDiscountOfferClick('A', 'forms_teaser');
  }, { capture: true });
  window.bsDiscountOfferTeaserClickListenerRegistered = true;
}

if (!window.bsDiscountOfferProductClickListenerRegistered) {
  document.addEventListener('click', (event) => {
    if (isHalloweenCampaignEnabled()) return;
    const trigger = event.target.closest?.('[data-discount-form-trigger]');
    if (!trigger) return;

    event.preventDefault();

    if (event.isTrusted === true && document.documentElement.dataset.discountPriceHintTestVariant === 'B') {
      const module = getDiscountPriceHintClickModule();
      if (module && module.contains(trigger) && !module.hidden && module.getClientRects().length > 0) {
        trackDiscountOfferClick('B', 'product_price_link');
      }
    }

    withFormTeaser((teaser) => teaser.click());
  });
  window.bsDiscountOfferProductClickListenerRegistered = true;
}
